// ─── STORE SETUP ──────────────────────────────────────────────────────────────
//
//  import recipesReducer from './recipesSlice';
//
//  configureStore({
//    reducer: {
//      recipes: recipesReducer,
//      // ...other reducers
//    },
//  });
//
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from './Navbar';
import Footer from './Footer';
import Componenet from './Componenet';

import { fetchRecipes, clearSelectedRecipe } from '../redux/recipesSlice';

const Recipe = () => {
  const dispatch = useDispatch();
  const { list: recipes, loading, error } = useSelector((s) => s.recipes);

  // Track which recipe is selected for popup (null = no popup)
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  useEffect(() => {
    dispatch(fetchRecipes());
    return () => dispatch(clearSelectedRecipe());
  }, [dispatch]);

  // Format multiline API strings (bullet/numbered) into JSX lines
  const formatText = (text = '') =>
    text.split('\n').filter(Boolean).map((line, i) => (
      <span key={i} style={{ display: 'block', marginBottom: '4px' }}>{line.trim()}</span>
    ));

  // Truncate text for card preview
  const truncateText = (text = '', maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Open popup with selected recipe
  const openPopup = (recipe) => {
    setSelectedRecipe(recipe);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  // Close popup
  const closePopup = () => {
    setSelectedRecipe(null);
    document.body.style.overflow = 'auto'; // Restore scrolling
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <Navbar />
        <main className="recipe-container">
          <h1 className="recipe-title">Recipes</h1>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '12px', color: '#666' }}>Loading recipes...</p>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <Navbar />
        <main className="recipe-container">
          <h1 className="recipe-title">Recipes</h1>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
            <h3 style={{ marginBottom: '8px' }}>Couldn't load recipes</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>{error}</p>
            <button
              onClick={() => dispatch(fetchRecipes())}
              style={{ padding: '10px 24px', background: '#000', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
            >
              Try Again
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Empty ──────────────────────────────────────────────────────────────────
  if (!recipes.length) {
    return (
      <>
        <Navbar />
        <main className="recipe-container">
          <h1 className="recipe-title">Recipes</h1>
          <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f8f9fa', borderRadius: '12px', margin: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🍽️</div>
            <h4 style={{ color: '#333', fontWeight: '500' }}>No recipes available yet</h4>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Recipe Cards Grid (Responsive) ─────────────────────────────────────────
  return (
    <>
      <Navbar />
      <main className="recipe-container" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        <h1 className="recipe-title" style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', textAlign: 'center' }}>Recipes</h1>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 'clamp(15px, 3vw, 25px)',
          marginTop: 'clamp(20px, 4vw, 30px)'
        }}>
          {recipes
            .filter((r) => r.status === 'published')
            .map((recipe) => (
              <div 
                key={recipe.id} 
                className="recipe-card"
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  cursor: 'pointer'
                }}
                onClick={() => openPopup(recipe)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                }}
              >
                {/* Image - Full width and height adjusted */}
                <div style={{
                  width: '100%',
                  height: 'clamp(180px, 25vw, 220px)',
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5'
                }}>
                  <img
                    src={recipe.image}
                    alt={recipe.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      backgroundColor: '#f5f5f5',
                      padding: '10px'
                    }}
                  />
                </div>

                {/* Content */}
                <div style={{
                  padding: 'clamp(15px, 4vw, 20px)',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {/* Title */}
                  <h3 style={{
                    fontSize: 'clamp(1.1rem, 4vw, 1.25rem)',
                    fontWeight: '600',
                    margin: '0 0 10px 0',
                    color: '#333',
                    lineHeight: '1.4'
                  }}>
                    {recipe.title}
                  </h3>

                  {/* Product tag */}
                  {recipe.product_name && (
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      background: '#f0f0f0',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      color: '#666',
                      marginBottom: '12px',
                      alignSelf: 'flex-start'
                    }}>
                      {recipe.product_name}
                    </span>
                  )}

                  {/* Ingredients Preview */}
                  <div style={{
                    marginBottom: '15px',
                    flex: 1
                  }}>
                    <p style={{
                      fontSize: '0.9rem',
                      color: '#666',
                      margin: 0,
                      lineHeight: '1.6'
                    }}>
                      <strong style={{ color: '#333' }}>Ingredients:</strong>{' '}
                      {truncateText(recipe.ingredients, 80)}
                    </p>
                  </div>

                  {/* View Details Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openPopup(recipe);
                    }}
                    style={{
                      padding: 'clamp(8px, 2vw, 10px) clamp(15px, 3vw, 20px)',
                      background: '#000',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: 'clamp(0.85rem, 3vw, 0.9rem)',
                      fontWeight: '500',
                      transition: 'background 0.2s ease',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#333';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#000';
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
        </div>
      </main>
      <Footer />

      {/* Popup Modal - Responsive */}
      {selectedRecipe && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: 'clamp(10px, 3vw, 20px)'
          }}
          onClick={closePopup}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: 'clamp(12px, 3vw, 16px)',
              maxWidth: 'min(900px, 95%)',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button - Responsive */}
            <button
              onClick={closePopup}
              style={{
                position: 'absolute',
                top: 'clamp(10px, 2vw, 15px)',
                right: 'clamp(10px, 2vw, 15px)',
                background: 'rgba(255,255,255,0.9)',
                border: 'none',
                fontSize: 'clamp(24px, 5vw, 28px)',
                cursor: 'pointer',
                color: '#333',
                width: 'clamp(35px, 8vw, 40px)',
                height: 'clamp(35px, 8vw, 40px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
              }}
            >
              ×
            </button>

            {/* Popup Content */}
            <div style={{ padding: 'clamp(20px, 5vw, 30px)' }}>
              {/* Image - Full visibility in popup */}
              <div style={{
                width: '100%',
                minHeight: 'clamp(200px, 30vw, 300px)',
                maxHeight: 'clamp(250px, 40vh, 400px)',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: 'clamp(20px, 4vw, 25px)',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <img
                  src={selectedRecipe.image}
                  alt={selectedRecipe.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'clamp(250px, 40vh, 400px)',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              </div>

              {/* Title - Responsive */}
              <h2 style={{
                fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                fontWeight: '600',
                marginBottom: '10px',
                color: '#333',
                wordWrap: 'break-word'
              }}>
                {selectedRecipe.title}
              </h2>

              {/* Product Tag */}
              {selectedRecipe.product_name && (
                <div style={{ marginBottom: '20px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: 'clamp(4px, 1.5vw, 6px) clamp(12px, 3vw, 16px)',
                    background: '#f0f0f0',
                    borderRadius: '30px',
                    fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                    color: '#555'
                  }}>
                    {selectedRecipe.product_name}
                  </span>
                </div>
              )}

              {/* Description if available */}
              {selectedRecipe.description && (
                <div style={{
                  marginBottom: 'clamp(20px, 4vw, 25px)',
                  padding: 'clamp(12px, 3vw, 15px)',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                  lineHeight: '1.6',
                  color: '#555'
                }}>
                  {selectedRecipe.description}
                </div>
              )}

              {/* Ingredients */}
              <div style={{ marginBottom: 'clamp(20px, 4vw, 25px)' }}>
                <h3 style={{
                  fontSize: 'clamp(1.2rem, 4vw, 1.4rem)',
                  fontWeight: '500',
                  marginBottom: 'clamp(12px, 2vw, 15px)',
                  color: '#333',
                  borderBottom: '2px solid #f0f0f0',
                  paddingBottom: '8px'
                }}>
                  Ingredients
                </h3>
                <div style={{
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                  lineHeight: '1.8',
                  color: '#555',
                  backgroundColor: '#fafafa',
                  padding: 'clamp(12px, 3vw, 15px)',
                  borderRadius: '8px',
                  wordWrap: 'break-word'
                }}>
                  {formatText(selectedRecipe.ingredients)}
                </div>
              </div>

              {/* Instructions */}
              <div style={{ marginBottom: 'clamp(20px, 4vw, 25px)' }}>
                <h3 style={{
                  fontSize: 'clamp(1.2rem, 4vw, 1.4rem)',
                  fontWeight: '500',
                  marginBottom: 'clamp(12px, 2vw, 15px)',
                  color: '#333',
                  borderBottom: '2px solid #f0f0f0',
                  paddingBottom: '8px'
                }}>
                  Preparation
                </h3>
                <div style={{
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                  lineHeight: '1.8',
                  color: '#555',
                  backgroundColor: '#fafafa',
                  padding: 'clamp(12px, 3vw, 15px)',
                  borderRadius: '8px',
                  wordWrap: 'break-word'
                }}>
                  {formatText(selectedRecipe.instructions)}
                </div>
              </div>

              {/* Additional Info - Responsive Grid */}
              {(selectedRecipe.prep_time || selectedRecipe.cook_time || selectedRecipe.servings) && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: 'clamp(10px, 2vw, 15px)',
                  marginTop: '20px',
                  padding: 'clamp(12px, 3vw, 15px)',
                  background: '#f0f0f0',
                  borderRadius: '8px'
                }}>
                  {selectedRecipe.prep_time && (
                    <div>
                      <strong style={{ color: '#333', display: 'block', marginBottom: '5px', fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>Prep Time</strong>
                      <span style={{ color: '#666', fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>{selectedRecipe.prep_time}</span>
                    </div>
                  )}
                  {selectedRecipe.cook_time && (
                    <div>
                      <strong style={{ color: '#333', display: 'block', marginBottom: '5px', fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>Cook Time</strong>
                      <span style={{ color: '#666', fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>{selectedRecipe.cook_time}</span>
                    </div>
                  )}
                  {selectedRecipe.servings && (
                    <div>
                      <strong style={{ color: '#333', display: 'block', marginBottom: '5px', fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>Servings</strong>
                      <span style={{ color: '#666', fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)' }}>{selectedRecipe.servings}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Recipe;